import { Button, Callout, DATE_FORMAT, Tag } from '@dev-spendesk/grapes';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ContactUs } from 'common/components/ContactUs';
import { ErrorState } from 'common/components/ErrorState';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useUserRoles } from 'modules/app/hooks/useUserRoles';
import { useGetBillingMigration } from 'modules/company/billing/components/CompanyPlan/hooks/useGetBillingMigration';
import { PendingKycEmptyState } from 'modules/kyc/components/PendingKycEmptyState';
import { useShouldCompleteKyc } from 'modules/kyc/hooks/useShouldCompleteKyc';
import { useUser } from 'src/core/modules/app/hooks/useUser';

import { CompanyPlan } from './CompanyPlan';
import { useGetCompanySubscriptionQuery } from './hooks/useGetCompanySubscriptionQuery';
import { useGetInvoicesQuery } from './hooks/useGetInvoicesQuery';
import { CompanySectionLoader } from '../../../components/CompanySectionLoader';
import {
  getCompanyPlanTranslationKey,
  isChargebeeSubscription,
} from '../../models';
import { PricingUpdateModal } from './components/PricingUpdateModal';

export const CompanyPlanContainer = () => {
  const { t, localeFormat } = useTranslation('global');
  const shouldCompleteKyc = useShouldCompleteKyc();
  const companyId = useCompanyId();
  const user = useUser();

  const billingMigrationQueryState = useGetBillingMigration(companyId);
  const getCompanySubscriptionQuery = useGetCompanySubscriptionQuery();
  const getInvoicesQuery = useGetInvoicesQuery();
  const { isAccountOwner } = useUserRoles();
  const [isPriceUpdateModalOpen, setIsPriceUpdateModalOpen] = useState(false);
  const { search } = useLocation();

  useEffect(() => {
    const queryParameters = new URLSearchParams(search);
    const priceUpdatePageParameter = queryParameters.get('priceUpdatePage');
    if (
      priceUpdatePageParameter === 'true' &&
      billingMigrationQueryState.status === 'success' &&
      billingMigrationQueryState.data?.migrationPending
    ) {
      setIsPriceUpdateModalOpen(true);
    }
  }, [search, billingMigrationQueryState]);

  if (shouldCompleteKyc) {
    return <PendingKycEmptyState />;
  }

  // Only show error state if both queries fail
  if (
    getCompanySubscriptionQuery.status === 'error' &&
    getInvoicesQuery.status === 'error'
  ) {
    return (
      <ErrorState
        title={t('billing.loadErrorTitle')}
        description={t('billing.loadErrorDescription')}
      />
    );
  }

  const shouldHideDescription =
    getCompanySubscriptionQuery.status === 'success' &&
    isChargebeeSubscription(getCompanySubscriptionQuery.data) &&
    getCompanySubscriptionQuery.data.plan.slug === 'subsidiary';

  return (
    <>
      <section aria-labelledby="companyPlan">
        {billingMigrationQueryState.status === 'success' &&
          billingMigrationQueryState.data?.migrationPending && (
            <>
              <Callout
                className="my-32"
                title={
                  <div>
                    <Trans
                      i18nKey="billing.companyPlan.priceUpdateCallout"
                      values={{
                        dueDate: localeFormat(
                          new Date(
                            billingMigrationQueryState.data.migrationPlan.migrationDate,
                          ),
                          DATE_FORMAT.MEDIUM,
                        ),
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setIsPriceUpdateModalOpen(true)}
                        className="bg-background-secondary-info-default title-m cursor-pointer border-none text-[inherit] underline"
                      >
                        -
                      </button>
                    </Trans>
                  </div>
                }
              />
              <PricingUpdateModal
                isOpen={isPriceUpdateModalOpen}
                setIsOpen={setIsPriceUpdateModalOpen}
                migrationPlan={billingMigrationQueryState.data.migrationPlan}
                userId={user.id}
              />
            </>
          )}

        {getInvoicesQuery.status === 'success' && (
          <>
            <h1
              id="companyPlan"
              className="text-content-primary heading-l flex items-center"
            >
              {getCompanySubscriptionQuery.status === 'success' &&
              isChargebeeSubscription(getCompanySubscriptionQuery.data) ? (
                <>
                  <span>{t('billing.companyPlan.title')}</span>
                  {getCompanySubscriptionQuery.data.plan.slug !==
                    'subsidiary' && (
                    <Tag className="ml-12 p-8" variant="purple">
                      <span className="title-l">
                        {t(
                          getCompanyPlanTranslationKey(
                            getCompanySubscriptionQuery.data.plan.slug,
                          ),
                        )}
                      </span>
                    </Tag>
                  )}
                </>
              ) : (
                t('billing.companyPlan.titleLegacy')
              )}
            </h1>
            {!shouldHideDescription && (
              <div className="text-secondary-bg-primary body-l mt-8 flex items-center justify-between">
                {t('billing.companyPlan.description')}
                {getCompanySubscriptionQuery.status === 'success' &&
                  isChargebeeSubscription(getCompanySubscriptionQuery.data) &&
                  isAccountOwner && (
                    <ContactUs>
                      {({ handleContactUs }) => (
                        <Button
                          className="ml-16 whitespace-nowrap"
                          variant="secondaryInfo"
                          text={t('billing.companyPlan.changePlan')}
                          onClick={() => {
                            handleContactUs();
                          }}
                        />
                      )}
                    </ContactUs>
                  )}
              </div>
            )}
          </>
        )}
        {(getCompanySubscriptionQuery.status === 'loading' ||
          getInvoicesQuery.status === 'loading') && <CompanySectionLoader />}
        {getInvoicesQuery.status === 'success' && (
          <CompanyPlan
            companySubscription={
              getCompanySubscriptionQuery.status === 'success'
                ? getCompanySubscriptionQuery.data
                : undefined
            }
            invoices={getInvoicesQuery.data}
            hasNextPage={getInvoicesQuery.hasNextPage}
            fetchNextPage={getInvoicesQuery.fetchNextPage}
          />
        )}
      </section>
    </>
  );
};
